import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { api } from "~/api";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/card";
import { SessionExpired } from "~/components/SessionExpired";
import { useAuth } from "~/contexts/auth";
import { useModal } from "~/contexts/modal";
import { useKeybindings } from "~/hooks/useKeyBindings";
import {
  type Cluster,
  type FleetOperation,
  type LatestDeployed,
  type ResolvedFleet,
} from "~/types";
import { cn, formatGitSha } from "~/utils";
import {
  DEFAULT_FLEET,
  activeFleetOperations,
  fleetTagOrder,
} from "~/utils/fleet";
import { parseFleetTag, rolloutGrouping } from "~/utils/rollout";

import { ClusterList } from "./components/ClusterList";
import { ClusterOperationsCommandPalette } from "./components/ClusterOperationsCommandPalette";
import { type ClusterType, FilterToggle } from "./components/FilterToggle";
import { Flash } from "./components/Flash";
import { FleetStatus } from "./components/FleetStatus";
import { ControlKbd, controlClass, ListSelect } from "./components/ListSelect";
import {
  type ClusterGroup,
  FIXED_GROUPINGS,
  UPGRADE_APPROVAL_GROUP,
  approvalGrouping,
  dimensionsFor,
  groupClusters,
  groupContaining,
  kindGrouping,
  sortClusters,
  valueFor,
  versionGrouping,
} from "./grouping";
import QueryInput, {
  QueryContext,
  defaultGroupByFor,
  useQueryParams,
} from "./QueryControl";

type NavigationMethod = "mouse" | "keyboard";

export default function Index() {
  const [clusters, setClusters] = useState<Cluster[]>([]);
  const [latestDeployed, setLatestDeployed] = useState<LatestDeployed | null>(
    null,
  );
  const target = latestDeployed?.commit ?? null;
  const master = latestDeployed?.master ?? null;
  // Resolves are keyed by the sha they were computed for; a resolve for a
  // moved-on sha reads as "not resolved yet".
  const [masterResolve, setMasterResolve] = useState<{
    sha: string;
    fleet: ResolvedFleet;
  } | null>(null);
  // Null until the first successful fetch: in-flight suppression can't be
  // trusted before we've actually seen the operations list.
  const [fleetOperations, setFleetOperations] = useState<
    FleetOperation[] | null
  >(null);
  const [tagOrder, setTagOrder] = useState<string[] | undefined>();
  const [deployedResolve, setDeployedResolve] = useState<{
    sha: string;
    fleet: ResolvedFleet;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Id-based so focus survives list reorders (search / grouping / the 2s poll).
  const [focusedClusterId, setFocusedClusterId] = useState<string | null>(null);
  const [selectedClusterIds, setSelectedClusterIds] = useState<Set<string>>(
    new Set(),
  );
  const [lastNavigationMethod, setLastNavigationMethod] =
    useState<NavigationMethod | null>(null);
  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false);
  const [pendingFleetOperation, setPendingFleetOperation] = useState<{
    waveLabel: string | null;
    target: "deployed" | "master";
  } | null>(null);
  const [sortBy, setSortBy] = useState("id");
  // Which list dropdown is open (`g`/`s` open them); list shortcuts pause while so.
  const [openMenu, setOpenMenu] = useState<"sort" | "group" | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [flash, setFlash] = useState<string | undefined>(location.state?.flash);
  const contextValue = useQueryParams();
  const { status, setIsAuthorized } = useAuth();
  const { isAnyModalOpen } = useModal();
  const {
    queryParams: { query, clusterType: selectedClusterType, groupBy },
    setQueryParams,
  } = contextValue;
  const ref = useRef<HTMLInputElement>(null);

  // Clear multi-select and focus when the type filter changes.
  useEffect(() => {
    setSelectedClusterIds(new Set());
    setFocusedClusterId(null);
  }, [selectedClusterType]);

  // Rollout and the type's default grouping are always valid, even before
  // any cluster carries the tag.
  const dimensions = useMemo(() => {
    const dims = new Set(dimensionsFor(clusters));
    dims.add("rollout");
    const defaultDim = defaultGroupByFor(selectedClusterType);
    if (defaultDim !== "none") dims.add(defaultDim);
    return [...dims];
  }, [clusters, selectedClusterType]);

  // Type + search filter, then optional grouping (reorders into contiguous
  // groups; `groups` carries the ranges).
  const { ordered: filteredClusters, groups } = useMemo(() => {
    let filtered = clusters;

    // Every tab but "all" is a backend `kind` tag. Untagged clusters match no
    // kind, so they surface only under "all", never miscategorized.
    if (selectedClusterType !== "all") {
      filtered = filtered.filter(
        (c) => valueFor(c, "kind") === selectedClusterType,
      );
    }

    // Then filter by search query
    if (query) {
      const searchTerms = query.toLowerCase().split(/\s+/).filter(Boolean);
      filtered = filtered.filter((cluster) => {
        const clusterId = cluster.id.toLowerCase();
        return searchTerms.every((term: string) => clusterId.includes(term));
      });
    }

    const sorted = sortClusters(filtered, sortBy);
    switch (groupBy) {
      case "none":
        return { ordered: sorted, groups: undefined };
      case "rollout":
        return groupClusters(sorted, groupBy, rolloutGrouping(tagOrder));
      case "version":
        return groupClusters(sorted, groupBy, versionGrouping(sorted));
      case UPGRADE_APPROVAL_GROUP:
        return groupClusters(sorted, groupBy, approvalGrouping("upgrade"));
      case "kind":
        return groupClusters(sorted, groupBy, kindGrouping());
      default:
        return groupClusters(sorted, groupBy);
    }
  }, [clusters, query, selectedClusterType, sortBy, groupBy, tagOrder]);

  // Labels manually toggled away from the dimension's default.
  const [toggledGroupLabels, setToggledGroupLabels] = useState<Set<string>>(
    new Set(),
  );
  // Reset on tab/dimension change so a prior "Expand all" doesn't resurface.
  useEffect(() => {
    setToggledGroupLabels(new Set());
  }, [selectedClusterType, groupBy]);
  // Customer view defaults to collapsed; every other grouping starts expanded.
  const groupsCollapsedByDefault = groupBy === "customer";
  const isGroupCollapsed = useCallback(
    (group: ClusterGroup) =>
      toggledGroupLabels.has(group.label)
        ? !groupsCollapsedByDefault
        : groupsCollapsedByDefault,
    [toggledGroupLabels, groupsCollapsedByDefault],
  );
  const toggleGroupCollapsed = useCallback((label: string) => {
    setToggledGroupLabels((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  }, []);
  // Drives the one expand/collapse toggle: anything short of fully expanded
  // expands, so a half-open view has an obvious next step.
  const allGroupsExpanded = useMemo(
    () => groups?.every((g) => !isGroupCollapsed(g)) ?? true,
    [groups, isGroupCollapsed],
  );
  // Toggle only the groups whose default disagrees with the target state.
  const setAllGroupsCollapsed = useCallback(
    (collapsed: boolean) => {
      setToggledGroupLabels(
        groupsCollapsedByDefault === collapsed
          ? new Set()
          : new Set(groups?.map((g) => g.label)),
      );
    },
    [groups, groupsCollapsedByDefault],
  );
  const toggleAllGroups = useCallback(
    () => setAllGroupsCollapsed(allGroupsExpanded),
    [allGroupsExpanded, setAllGroupsCollapsed],
  );
  // Each tab lands on its own default grouping: a choice made under one tab
  // (including "no grouping") would otherwise decide how the next one opens.
  // Re-picking the current tab keeps the view as it is.
  const selectClusterType = useCallback(
    (type: ClusterType) => {
      if (type === selectedClusterType) return;
      setQueryParams({ clusterType: type, groupBy: "" });
    },
    [selectedClusterType, setQueryParams],
  );

  const sortOptions = useMemo(
    () => [
      { value: "id", label: "sort by name" },
      { value: "updated_at", label: "sort by updated" },
      { value: "commit_time", label: "sort by version" },
      ...dimensions.map((d) => ({ value: d, label: `sort by ${d}` })),
    ],
    [dimensions],
  );
  const groupOptions = useMemo(
    () => [
      { value: "none", label: "no grouping" },
      { value: "version", label: "group by version" },
      { value: UPGRADE_APPROVAL_GROUP, label: "group by upgrade approval" },
      ...dimensions.map((d) => ({ value: d, label: `group by ${d}` })),
    ],
    [dimensions],
  );

  // Drop selections that fall out of view (search narrows, poll removes) so
  // the selection never outlives the visible list.
  useEffect(() => {
    setSelectedClusterIds((prev) => {
      if (prev.size === 0) return prev;
      const next = new Set(
        filteredClusters.filter((c) => prev.has(c.id)).map((c) => c.id),
      );
      return next.size === prev.size ? prev : next;
    });
  }, [filteredClusters]);

  // Reset grouping/sort if the chosen dimension disappears (e.g. on a poll).
  // Falls back to the type's own default, not a hardcoded "none"; the fixed
  // groupings are exempt since they're real choices, not tag dimensions.
  useEffect(() => {
    if (clusters.length === 0) return;
    const fallbackGroupBy = defaultGroupByFor(selectedClusterType);
    if (
      !FIXED_GROUPINGS.includes(groupBy) &&
      groupBy !== fallbackGroupBy &&
      !dimensions.includes(groupBy)
    )
      setQueryParams({ groupBy: fallbackGroupBy });
    const tagSort = !["id", "updated_at", "commit_time"].includes(sortBy);
    if (tagSort && !dimensions.includes(sortBy)) setSortBy("id");
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setQueryParams closes over the latest search params; the checks only depend on these
  }, [clusters.length, dimensions, groupBy, sortBy, selectedClusterType]);

  // The focused cluster's current row, or null if it's filtered out.
  const selectedClusterItemIndex = useMemo(() => {
    if (focusedClusterId === null) return null;
    const i = filteredClusters.findIndex((c) => c.id === focusedClusterId);
    return i === -1 ? null : i;
  }, [filteredClusters, focusedClusterId]);

  // Clear focus if its group collapses — keyboard focus is protected in
  // ClusterList, mouse focus isn't.
  useEffect(() => {
    if (lastNavigationMethod !== "mouse") return;
    if (selectedClusterItemIndex === null || !groups) return;
    const group = groupContaining(groups, selectedClusterItemIndex);
    if (group && isGroupCollapsed(group)) setFocusedClusterId(null);
  }, [
    groups,
    selectedClusterItemIndex,
    isGroupCollapsed,
    lastNavigationMethod,
  ]);

  // Multi-select helpers. toggleSelection is the primitive: select a set of
  // clusters, or clear them if they're already all selected.
  const toggleSelection = useCallback((ids: string[]) => {
    setSelectedClusterIds((prev) => {
      const allSelected = ids.length > 0 && ids.every((id) => prev.has(id));
      const next = new Set(prev);
      for (const id of ids) {
        if (allSelected) next.delete(id);
        else next.add(id);
      }
      return next;
    });
  }, []);

  const toggleClusterSelection = useCallback(
    (clusterId: string) => toggleSelection([clusterId]),
    [toggleSelection],
  );

  const clearAllSelections = useCallback(() => {
    setSelectedClusterIds(new Set());
  }, []);

  // `a`: every visible cluster. `Shift+G` (or clicking a header): the focused group.
  const selectAllVisible = useCallback(() => {
    toggleSelection(filteredClusters.map((c) => c.id));
  }, [filteredClusters, toggleSelection]);

  const selectFocusedGroup = useCallback(() => {
    if (!groups || selectedClusterItemIndex === null) return;
    const g = groupContaining(groups, selectedClusterItemIndex);
    if (g) toggleSelection(g.clusters.map((c) => c.id));
  }, [groups, selectedClusterItemIndex, toggleSelection]);

  const selectedClusters = useMemo(() => {
    return filteredClusters.filter((cluster) =>
      selectedClusterIds.has(cluster.id),
    );
  }, [filteredClusters, selectedClusterIds]);

  const targetSha = target?.sha;
  const masterSha = master?.sha;
  const resolvedFleet =
    deployedResolve && deployedResolve.sha === targetSha
      ? deployedResolve.fleet
      : null;
  const resolvedMaster =
    masterResolve && masterResolve.sha === masterSha
      ? masterResolve.fleet
      : null;
  const inFlightFleetOperations = useMemo(
    () => (fleetOperations ? activeFleetOperations(fleetOperations) : []),
    [fleetOperations],
  );
  // Only unfiltered all-cluster rollout rows get the planned marker.
  const plannedClusterIds = useMemo(
    () =>
      groupBy === "rollout" &&
      selectedClusterType === "all" &&
      !query &&
      resolvedFleet
        ? new Set(resolvedFleet.waves.flatMap((wave) => wave.clusters))
        : undefined,
    [groupBy, selectedClusterType, query, resolvedFleet],
  );

  const openUpgrade = useCallback(
    (only?: ClusterGroup, which: "deployed" | "master" = "deployed") => {
      const commit = which === "master" ? master : target;
      const fleet = which === "master" ? resolvedMaster : resolvedFleet;
      if (!commit || !fleet) return;
      setPendingFleetOperation({
        waveLabel: only?.label ?? null,
        target: which,
      });
      setIsCommandPaletteOpen(true);
    },
    [target, master, resolvedFleet, resolvedMaster],
  );

  const fleetPalette = useMemo(() => {
    if (!pendingFleetOperation) return null;
    const commit = pendingFleetOperation.target === "master" ? master : target;
    const fleet =
      pendingFleetOperation.target === "master"
        ? resolvedMaster
        : resolvedFleet;
    if (!commit || !fleet) return null;
    const { waveLabel } = pendingFleetOperation;
    let waves;
    let payload;
    if (waveLabel === null) {
      waves = fleet.waves;
      payload = { fleet: DEFAULT_FLEET };
    } else {
      // Group labels are bare rollout values; wave labels are key:value.
      const wave = fleet.waves.find((candidate) => {
        const parsed = parseFleetTag(candidate.label);
        return parsed?.key === "rollout" && parsed.value === waveLabel;
      });
      if (!wave) return null;
      waves = [wave];
      payload = { targets: [wave.clusters] };
    }
    const clusterIds = new Set(waves.flatMap((wave) => wave.clusters));
    return {
      sha: commit.sha,
      clusters: clusters.filter((cluster) => clusterIds.has(cluster.id)),
      operation: {
        payload,
        waves,
        description: `${waveLabel ?? "fleet"} upgrade → ${formatGitSha(commit.sha)}`,
      },
    };
  }, [
    pendingFleetOperation,
    resolvedFleet,
    resolvedMaster,
    target,
    master,
    clusters,
  ]);

  const fetchClusters = useCallback(() => {
    api
      .getClusters()
      .then(setClusters)
      .catch((err) => {
        if (err.status === 401) {
          setIsAuthorized(false);
        } else {
          setError(err.message);
        }
      });
    api
      .getLatestDeployed()
      .then(setLatestDeployed)
      .catch((err) => console.warn("Failed to fetch latest deployed:", err));
  }, [setIsAuthorized]);

  const fetchFleetOperations = useCallback(() => {
    api
      .getFleetOperations()
      .then(setFleetOperations)
      .catch((err) => {
        if (err.status === 401) setIsAuthorized(false);
      });
  }, [setIsAuthorized]);

  // Guards stale resolves: a late response only applies if no newer fetch
  // started since (the shas may have moved on).
  const resolveGeneration = useRef(0);
  const fetchResolvedFleet = useCallback(() => {
    const generation = ++resolveGeneration.current;
    const fresh = () => generation === resolveGeneration.current;
    const fail = (err: { status?: number }) => {
      if (err.status === 401) setIsAuthorized(false);
    };
    if (targetSha) {
      api
        .resolveFleet(DEFAULT_FLEET, targetSha)
        .then((fleet) => {
          if (!fresh()) return;
          setDeployedResolve({ sha: targetSha, fleet });
          if (masterSha === targetSha) {
            setMasterResolve({ sha: masterSha, fleet });
          }
        })
        .catch(fail);
    } else {
      setDeployedResolve(null);
    }
    if (masterSha && masterSha !== targetSha) {
      api
        .resolveFleet(DEFAULT_FLEET, masterSha)
        .then((fleet) => {
          if (fresh()) setMasterResolve({ sha: masterSha, fleet });
        })
        .catch(fail);
    } else if (!masterSha) {
      setMasterResolve(null);
    }
  }, [targetSha, masterSha, setIsAuthorized]);

  useEffect(() => {
    let active = true;
    api
      .getFleet(DEFAULT_FLEET)
      .then((fleet) => {
        if (active) setTagOrder(fleetTagOrder(fleet));
      })
      .catch((err) => {
        if (!active) return;
        setTagOrder(undefined);
        if (err.status === 401) setIsAuthorized(false);
      });
    return () => {
      active = false;
    };
  }, [setIsAuthorized]);

  useEffect(() => {
    fetchFleetOperations();
    const interval = setInterval(() => {
      if (!isCommandPaletteOpen && document.hasFocus()) {
        fetchFleetOperations();
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [fetchFleetOperations, isCommandPaletteOpen]);

  // Resolving hits GitHub server-side, so poll it gently; a new target sha
  // re-runs this effect and resolves immediately.
  useEffect(() => {
    fetchResolvedFleet();
    const interval = setInterval(() => {
      if (!isCommandPaletteOpen && document.hasFocus()) {
        fetchResolvedFleet();
      }
    }, 10000);
    return () => clearInterval(interval);
  }, [fetchResolvedFleet, isCommandPaletteOpen]);

  useEffect(() => {
    fetchClusters();

    const interval = setInterval(() => {
      if (!isCommandPaletteOpen && document.hasFocus()) {
        fetchClusters();
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [isCommandPaletteOpen, fetchClusters]);

  useEffect(() => {
    if (location.state?.flash) {
      setFlash(location.state.flash);

      // Clear the navigation state to prevent the flash from reappearing
      // when the user manually navigates back to this page
      navigate("/", { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  // Move focus by one row (wrapping) and pin it to that cluster's id.
  const moveFocus = useCallback(
    (delta: 1 | -1) => {
      const n = filteredClusters.length;
      if (n === 0) return;
      const cur = selectedClusterItemIndex;
      const next =
        cur === null ? (delta > 0 ? 0 : n - 1) : (cur + delta + n) % n;
      setFocusedClusterId(filteredClusters[next].id);
      setLastNavigationMethod("keyboard");
    },
    [filteredClusters, selectedClusterItemIndex],
  );

  const escapeClusterItemFocus = () => {
    setFocusedClusterId(null);
  };

  const handleClusterSelect = useCallback(
    (index: number, method: NavigationMethod, isShiftClick = false) => {
      setFocusedClusterId(filteredClusters[index]?.id ?? null);
      setLastNavigationMethod(method);
      if (isShiftClick && filteredClusters[index]) {
        toggleClusterSelection(filteredClusters[index].id);
      }
    },
    [filteredClusters, toggleClusterSelection],
  );

  // Hover focus lives only while the pointer is on a row: otherwise the
  // pointer resting anywhere in the list leaves a stale `$mod+k` target.
  const handleClusterHoverEnd = useCallback(() => {
    // The palette's overlay covers the row, so its own mouseleave must not
    // pull the target out from under it.
    if (lastNavigationMethod === "mouse" && !isCommandPaletteOpen) {
      setFocusedClusterId(null);
    }
  }, [lastNavigationMethod, isCommandPaletteOpen]);

  const handleCardContentMouseLeave = useCallback(() => {
    // only clear the focus if command palette is not open
    if (lastNavigationMethod === "mouse" && !isCommandPaletteOpen) {
      setFocusedClusterId(null);
    }
  }, [lastNavigationMethod, isCommandPaletteOpen]);

  useKeybindings(
    useMemo(
      () => ({
        "/": (event: KeyboardEvent) => {
          // prevent the "/" from being typed into the input field
          event.preventDefault();
          escapeClusterItemFocus();
          ref.current?.focus();
        },
        j: () => moveFocus(1),
        k: () => moveFocus(-1),
        ArrowDown: (e: KeyboardEvent) => {
          e.preventDefault();
          moveFocus(1);
        },
        ArrowUp: (e: KeyboardEvent) => {
          e.preventDefault();
          moveFocus(-1);
        },
        Enter: () => {
          if (
            selectedClusterItemIndex !== null &&
            filteredClusters[selectedClusterItemIndex]
          ) {
            // If we're in selection mode, toggle selection instead of navigating
            if (selectedClusterIds.size > 0) {
              const clusterId = filteredClusters[selectedClusterItemIndex].id;
              toggleClusterSelection(clusterId);
            } else {
              sessionStorage.setItem(
                "clusterListScrollPos",
                window.scrollY.toString(),
              );
              navigate(
                `/clusters/${filteredClusters[selectedClusterItemIndex].id}`,
              );
            }
          }
        },
        x: (e: KeyboardEvent) => {
          e.preventDefault();
          if (
            selectedClusterItemIndex !== null &&
            filteredClusters[selectedClusterItemIndex]
          ) {
            const clusterId = filteredClusters[selectedClusterItemIndex].id;
            toggleClusterSelection(clusterId);
          }
        },
        Escape: (e: KeyboardEvent) => {
          if (e.defaultPrevented) return; // ignore Esc that closed the dialog
          if (selectedClusterIds.size > 0) {
            clearAllSelections();
          } else {
            escapeClusterItemFocus();
          }
        },
        "$mod+k": (e: KeyboardEvent) => {
          e.preventDefault(); // prevent Firefox from opening the search bar
          setIsCommandPaletteOpen(true);
        },
        "1": () => selectClusterType("all"),
        "2": () => selectClusterType("mt"),
        "3": () => selectClusterType("st"),
        "4": () => selectClusterType("byoc"),
        g: (e: KeyboardEvent) => {
          e.preventDefault();
          setOpenMenu("group");
        },
        s: (e: KeyboardEvent) => {
          e.preventDefault();
          setOpenMenu("sort");
        },
        e: (event: KeyboardEvent) => {
          event.preventDefault();
          toggleAllGroups();
        },
        a: (e: KeyboardEvent) => {
          e.preventDefault();
          selectAllVisible();
        },
        "$mod+a": (e: KeyboardEvent) => {
          e.preventDefault(); // prevent the browser from selecting the page text
          selectAllVisible();
        },
        "Shift+G": (e: KeyboardEvent) => {
          e.preventDefault();
          selectFocusedGroup();
        },
      }),
      [
        filteredClusters,
        selectedClusterItemIndex,
        selectedClusterIds.size,
        navigate,
        moveFocus,
        toggleClusterSelection,
        clearAllSelections,
        selectAllVisible,
        selectFocusedGroup,
        toggleAllGroups,
        selectClusterType,
      ],
    ),
    {
      // disable while a modal or a list dropdown is open
      enabled: !isCommandPaletteOpen && !isAnyModalOpen && openMenu === null,
    },
  );

  // when focus is in search input field
  useKeybindings(
    useMemo(
      () => ({
        Escape: () => {
          if (ref.current) {
            ref.current.blur();
            setQueryParams({ query: "" });
          }
        },
        ArrowDown: (e: KeyboardEvent) => {
          e.preventDefault();
          ref.current?.blur();
          moveFocus(1);
        },
        Enter: () => {
          if (filteredClusters.length > 0) {
            ref.current?.blur();
            moveFocus(1);
          }
        },
      }),
      [ref, filteredClusters, moveFocus, setQueryParams],
    ),
    {
      allowedElements: ["INPUT"],
      disallowedElements: [],
      enabled: !isCommandPaletteOpen && !isAnyModalOpen, // disable when any modal is open
    },
  );

  if (status !== "authorized") {
    return <SessionExpired />;
  }

  if (error) {
    return <div className="text-red-500">Error: {error}</div>;
  }

  return (
    <>
      <Flash flash={flash} />
      <Card>
        <CardHeader>
          <CardTitle>tpuf-ctl-vendor</CardTitle>
        </CardHeader>
        <CardContent onMouseLeave={handleCardContentMouseLeave}>
          <QueryContext.Provider value={contextValue}>
            {selectedClusterIds.size > 0 ? (
              <div className="mb-4 flex flex-col gap-3 rounded-md border border-blue-200 bg-blue-50 p-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-blue-800">
                    {selectedClusterIds.size} cluster(s) selected
                  </span>
                  <button
                    onClick={clearAllSelections}
                    className="text-xs text-blue-600 underline hover:text-blue-800"
                  >
                    Clear selection
                  </button>
                </div>
                <button
                  onClick={() => setIsCommandPaletteOpen(true)}
                  className="w-full rounded bg-blue-600 px-3 py-2 text-sm text-white hover:bg-blue-700 sm:w-auto sm:py-1"
                >
                  <span className="hidden sm:inline">Run Operations (⌘K)</span>
                  <span className="sm:hidden">Run Operations</span>
                </button>
              </div>
            ) : null}
            <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center">
              <QueryInput className="flex-1" ref={ref} />
              <FilterToggle
                selectedType={selectedClusterType}
                onTypeChange={selectClusterType}
                className="w-full sm:w-auto"
              />
              <ListSelect
                options={sortOptions}
                value={sortBy}
                onChange={setSortBy}
                kbd="s"
                open={openMenu === "sort"}
                onOpenChange={(o) => setOpenMenu(o ? "sort" : null)}
                className="w-full sm:w-auto"
              />
              <ListSelect
                options={groupOptions}
                value={groupBy}
                onChange={(value) => setQueryParams({ groupBy: value })}
                kbd="g"
                open={openMenu === "group"}
                onOpenChange={(o) => setOpenMenu(o ? "group" : null)}
                className="w-full sm:w-auto"
              />
              {/* A third peer of sort and group, only while there are groups. */}
              {groups ? (
                <button
                  type="button"
                  onClick={toggleAllGroups}
                  className={cn(controlClass, "w-full sm:w-auto")}
                >
                  {allGroupsExpanded ? "collapse all" : "expand all"}
                  <ControlKbd>e</ControlKbd>
                </button>
              ) : null}
            </div>
            {fleetOperations ? (
              <FleetStatus
                activeOperations={inFlightFleetOperations}
                deployedFleet={resolvedFleet}
                masterFleet={resolvedMaster}
                latest={latestDeployed}
                clusters={clusters}
                onUpgrade={(which) => openUpgrade(undefined, which)}
              />
            ) : null}
            <ClusterList
              clusters={filteredClusters}
              groups={groups}
              isGroupCollapsed={isGroupCollapsed}
              onToggleGroupCollapsed={toggleGroupCollapsed}
              plannedClusterIds={plannedClusterIds}
              onUpgradeGroup={
                // A live fleet op suppresses group upgrades like the banner.
                resolvedFleet && inFlightFleetOperations.length === 0
                  ? openUpgrade
                  : undefined
              }
              onSelectGroup={toggleSelection}
              selectedIndex={selectedClusterItemIndex}
              selectedIndexFromKeyboard={lastNavigationMethod === "keyboard"}
              selectedClusterIds={selectedClusterIds}
              onHover={(index) => handleClusterSelect(index, "mouse")}
              onHoverEnd={handleClusterHoverEnd}
              onFocus={(index) => handleClusterSelect(index, "keyboard")}
              onToggleSelection={toggleClusterSelection}
              onShiftClick={(index) =>
                handleClusterSelect(index, "mouse", true)
              }
            />
          </QueryContext.Provider>
        </CardContent>
      </Card>

      {isCommandPaletteOpen ? (
        <ClusterOperationsCommandPalette
          isOpen={isCommandPaletteOpen}
          setIsOpen={(open) => {
            setIsCommandPaletteOpen(open);
            if (!open) {
              setPendingFleetOperation(null);
            }
          }}
          clusters={
            fleetPalette
              ? fleetPalette.clusters
              : selectedClusters.length > 0
                ? selectedClusters
                : selectedClusterItemIndex !== null &&
                    filteredClusters[selectedClusterItemIndex]
                  ? [filteredClusters[selectedClusterItemIndex]]
                  : []
          }
          initialOperation={fleetPalette ? "upgrade" : undefined}
          initialArguments={
            fleetPalette ? { target_version: fleetPalette.sha } : undefined
          }
          fleetOperation={fleetPalette?.operation}
          onOperationComplete={() => {
            fetchClusters();
            fetchFleetOperations();
          }}
        />
      ) : null}
    </>
  );
}
